import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { notifications } from "@mantine/notifications";
import { api, setUnauthorizedHandler } from "./api";

interface AuthState {
  status: "loading" | "anonymous" | "authenticated";
  email: string | null;
}

interface AuthCtx extends AuthState {
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
  // Called by /api/auth/verify-otp success — flips state to authenticated
  // without a second roundtrip.
  markAuthenticated: (email: string) => void;
}

const Ctx = createContext<AuthCtx>({
  status: "loading",
  email: null,
  refresh: async () => {},
  logout: async () => {},
  markAuthenticated: () => {},
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: "loading", email: null });

  const refresh = useCallback(async () => {
    try {
      const r = await api.whoami();
      if (r.authenticated && r.email) {
        setState({ status: "authenticated", email: r.email });
      } else {
        setState({ status: "anonymous", email: null });
      }
    } catch {
      setState({ status: "anonymous", email: null });
    }
  }, []);

  const logout = useCallback(async () => {
    try { await api.auth.logout(); } catch { /* ignore */ }
    setState({ status: "anonymous", email: null });
  }, []);

  const markAuthenticated = useCallback((email: string) => {
    setState({ status: "authenticated", email });
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // If any authenticated API call ever returns 401 mid-session, flip the
  // app back to anonymous so OtpLogin re-renders. We:
  //   1. Skip the flip if we're already anonymous (no spurious toasts during
  //      the initial whoami probe).
  //   2. Surface a toast so the user knows WHY the screen just changed —
  //      otherwise it looks like the app crashed mid-typing.
  useEffect(() => {
    let lastShown = 0;
    setUnauthorizedHandler(() => {
      setState((prev) => {
        if (prev.status !== "authenticated") return prev;
        const now = Date.now();
        if (now - lastShown > 2000) {
          lastShown = now;
          notifications.show({
            color: "orange",
            title: "Signed out",
            message: "Your session expired. Please sign in again.",
            autoClose: 6000,
          });
        }
        return { status: "anonymous", email: null };
      });
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  // Re-check session when the tab regains focus — catches the case where the
  // session expired in the background.
  useEffect(() => {
    const onFocus = () => { void refresh(); };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refresh]);

  return (
    <Ctx.Provider value={{ ...state, refresh, logout, markAuthenticated }}>
      {children}
    </Ctx.Provider>
  );
}

export function useAuth() {
  return useContext(Ctx);
}
